<script>
  import { format } from 'svelte-i18n';

  import { Icon } from '@client/components/icon';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import { TagBlocksService } from '@client/services/tag-blocks';

  import { buildTemplateSignatureData, loadStudioTemplatePreviews } from '@client/stores/signature-templates';
  import { userStore } from '@client/stores/user';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let selectedTemplate = null;
  let previewTemplates = [];
  let loaded = false;

  const loadPreviews = async () => {
    try {
      previewTemplates = await loadStudioTemplatePreviews();
    } catch (error) {
      notify($format('label.FAILURE.LOAD_SIGNATURE_TEMPLATES'), 'error');
    }
  };

  // Load previews once, when the smart-host identity (cname = [proto, host]) is ready — without
  // it the icon/photo URLs would render as `undefined://undefined/...`. The created signature is
  // rebuilt fresh from current brand at create time (see executeCreate), so the preview is
  // display-only and needs no reactive reload (mirrors the business-card gallery).
  $: if ($userStore?.cname?.[1] && !loaded) {
    loaded = true;
    loadPreviews();
  }

  const onSelectTemplate = (template) => {
    selectedTemplate = template;
  };

  const executeCreate = async () => {
    // Rebuild the payload from the CURRENT brand at create time (by id, inside the store) so a
    // preview rendered earlier never bakes a stale/fallback brand into the created signature.
    const data = buildTemplateSignatureData(selectedTemplate);

    const response = await TagBlocksService.create(data);

    if (response.status >= 400) {
      notify($format('label.FAILURE.CREATE_SIGNATURE'), 'error');
      throw new Error($format('label.FAILURE.CREATE_SIGNATURE'));
    }

    notify($format('label.SUCCESS.SIGNATURE_CREATION', { values: { name: response.rulename } }));

    step.data = {
      id: response.rulename,
      rollbackData: {
        createdSignature: response
      }
    };
  };

  const rollbackCreate = async () => {
    if (!step.data?.rollbackData) {
      throw new Error($format('label.NO_ROLLBACK_DATA'));
    }

    const { createdSignature } = step.data.rollbackData;

    const response = await TagBlocksService.delete(createdSignature);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ROLLBACK_SIGNATURE_CREATE'), 'error');
      throw new Error($format('label.FAILURE.ROLLBACK_SIGNATURE_CREATE'));
    }

    notify($format('label.SUCCESS.ROLLBACK_SIGNATURE_CREATE'));

    return { success: true };
  };

  if (onExecute) {
    onExecute(executeCreate);
  }

  if (onRollback) {
    onRollback(rollbackCreate);
  }

  $: nextDisabled = selectedTemplate === null;
</script>

<div class="signature-create-form">
  <div class="template-grid">
    <!-- Blank Template -->
    <button
      class="template-card"
      class:selected={selectedTemplate === false}
      on:click={() => onSelectTemplate(false)}
      on:keydown={(e) => e.key === 'Enter' && onSelectTemplate(false)}
      tabindex="0"
    >
      <div class="template-preview blank">
        <Icon name="file" />
      </div>
      <div class="template-info">
        <h4>{$format('label.BLANK_SIGNATURE')}</h4>
        <p>{$format('label.START_FROM_SCRATCH')}</p>
      </div>
    </button>

    <!-- Template Cards -->
    {#each previewTemplates as template, index}
      <button
        class="template-card"
        class:selected={selectedTemplate?.id === template.id}
        on:click={() => onSelectTemplate(template)}
        on:keydown={(e) => e.key === 'Enter' && onSelectTemplate(template)}
        tabindex="0"
      >
        <div class="template-preview">
          <iframe
            tabindex="-1"
            style="width: 100%; height: {template.height}px;"
            scrolling="no"
            sandbox="allow-same-origin"
            title={template.name}
            srcdoc="<html><body><div style='transform: scale(.5,.5); transform-origin: left center; -moz-transform:  scale(.5,.5); -moz-transform-origin: left center;'>{template.html}</div></body></html>"
          />
        </div>
        <div class="template-info">
          <h4>{template.name}</h4>
        </div>
      </button>
    {/each}
  </div>
</div>

<style lang="scss">
  .signature-create-form {
    padding: var(--padding-base);
  }

  .template-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 12px;
  }

  .template-card {
    background: var(--color-white);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-large);
    cursor: pointer;
    overflow: hidden;
    padding: 0;
    text-align: left;
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 0 0 4px var(--color-gray-100);
    }

    &.selected {
      background-color: var(--color-blue-50);
      border-color: var(--color-blue-400);
    }

    &:focus {
      border-color: var(--color-blue-400);
      box-shadow: 0 0 0 3px var(--color-blue-400);
      outline: none;
    }
  }

  .template-preview {
    align-items: center;
    background: var(--color-gray-50);
    border-bottom: 1px solid var(--border-color);
    display: flex;
    height: 160px;
    justify-content: center;
    overflow: hidden;
    position: relative;

    &.blank {
      :global(i) {
        color: var(--color-gray-400);
        font-size: 64px;
      }
    }

    iframe {
      border: none;
      pointer-events: none;
      transform-origin: left top;
    }
  }

  .template-info {
    padding: var(--padding-large);

    h4 {
      color: var(--color-gray-900);
      font-size: 14px;
      font-weight: 600;
      margin: 0 0 4px;
    }

    p {
      color: var(--color-gray-600);
      font-size: 12px;
      margin: 0;
    }
  }

  .template-card.selected .template-preview {
    background-color: var(--color-blue-50);
  }
</style>
